import { useState } from "react";
import { Mail, Instagram, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you within 24-48 hours ✨");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
      <div className="font-script text-3xl text-[#2eb6a8]">We Love Mail</div>
      <h1 className="mt-1 font-display text-5xl sm:text-7xl font-black text-[#1c1815] tracking-tight">
        <em className="italic text-[#e8478e]">Contact</em> Us
      </h1>
      <p className="mt-4 text-[#4a4239] max-w-xl">Questions, custom orders, collabs, or just want to say hi? Drop a line below.</p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <form onSubmit={submit} className="md:col-span-2 space-y-4 p-7 sm:p-9 rounded-3xl border border-[#e5dccd] bg-white shadow-sm" data-testid="contact-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Your Name</label>
            <input
              required
              data-testid="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Email</label>
            <input
              type="email" required
              data-testid="contact-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Message</label>
            <textarea
              required rows={5}
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none resize-y text-[#1c1815]"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-3.5 rounded-full transition-all disabled:opacity-60"
          >
            <Send size={14}/> {sending ? "Sending…" : "Send Message"}
          </button>
        </form>

        <aside className="space-y-4">
          <a href="mailto:support@sniphappensboutique.com" className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-[#e5dccd] hover:border-[#2eb6a8] hover:shadow-md transition-all" data-testid="contact-email-link">
            <Mail className="text-[#2eb6a8]" size={20}/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Email</div>
              <div className="font-bold text-[#1c1815] break-all">support@sniphappensboutique.com</div>
            </div>
          </a>
          <a href="#" className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-[#e5dccd] hover:border-[#e8478e] hover:shadow-md transition-all">
            <Instagram className="text-[#e8478e]" size={20}/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Instagram</div>
              <div className="font-bold text-[#1c1815]">@sniphappensboutique</div>
            </div>
          </a>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-[#2eb6a8]/10 to-[#e8478e]/10 border border-[#e5dccd]">
            <div className="font-script text-2xl text-[#2eb6a8]">Response Time</div>
            <p className="text-sm text-[#4a4239] mt-1">We answer all messages within 24-48 hours, usually faster if there's coffee involved.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
